import { Button } from "@windmill/react-ui";
import React, { useEffect, useState } from "react";
import { notifyError } from "../../utils/toast";
import ProgressBar from "../progressbar/ProgressBar";
import OnBoardingContainer from "./onBoardingContainer";
import Logo from "../logo/Logo";
import { BiChevronLeft } from "react-icons/bi";
import { fetchIndustryData } from "../../data/industriesData";

export default function AboutBusiness({ setStep, setStoreData, storeData }) {
  const [showSubIndustries, setShowSubIndustries] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [industries, setIndustries] = useState([]);

  const loadIndustryData = async () => {
    const indData = await fetchIndustryData();
    setIndustries(indData);
  };

  // Fetch initial reports
  useEffect(() => {
    loadIndustryData();
  }, []);

  // Find selected industry object when there is change:
  useEffect(() => {
    if (storeData.industry) {
      const industry = industries?.find(
        (ind) => ind.id == storeData.industry
      );
      setSelectedIndustry(industry);
      // Reset sub-industry if industry changes
      if (
        storeData.sub_industry &&
        industry &&
        !industry?.subIndustries?.some(
          (sub) => sub.id == storeData.sub_industry
        )
      ) {
        setStoreData((prev) => ({
          ...prev,
          sub_industry: null,
          sub_industry_Name: null,
        }));
      }
    }
  }, [industries, storeData.industry]);

  // Handle primary industry selection
  const handleIndustrySelect = (industryId) => {
    setStoreData((prev) => ({
      ...prev,
      industry: industryId,
    }));

    // Don't immediately show sub-industries - first confirm primary selection
    setShowSubIndustries(false);
  };

  // Handle sub-industry selection
  const handleSubIndustrySelect = (subIndustry) => {
    setStoreData((prev) => ({
      ...prev,
      sub_industry: subIndustry.id,
      sub_industry_name: subIndustry.name,
    }));
  };

  // Continue to next step or show sub-industries
  const handleContinue = () => {
    if (!storeData.industry) {
      notifyError("Please select a your industry type");
      return;
    }

    if (!showSubIndustries) {
      setShowSubIndustries(true);
      return;
    }

    if (!storeData.sub_industry) {
      notifyError("Please select a specific category");
      return;
    }

    // Move to next step
    setStep((prev) => prev + 1);
  };

  return (
    <div className="h-screen bg-green-50">
      <div className="max-w-4xl mx-auto py-12">
        <OnBoardingContainer>
          <div className="text-left p-6 lg:p-8 lg:px-10 pt-6">
          <Logo />
            <ProgressBar level={"40%"} />

            {/* Primary Industry Selection */}
            {!showSubIndustries && (
              <div>
                <p className="text-lg font-medium mt-6">
                  What industry is your company, team, or department in?
                </p>
                <p className="text-sm mt-1">
                  Select the primary industry that best describes your
                  organization: You can only select one.
                </p>

                <div className="mt-6">
                  <div className="grid lg:grid-cols-2 gap-4">
                    {industries?.map((option) => (
                      <label
                        onClick={() => handleIndustrySelect(option.id)}
                        htmlFor={option.id}
                        key={option.id}
                        className="border border-gray-200 rounded block cursor-pointer transition-all hover:shadow-md"
                      >
                        <div
                          className={`px-4 py-3 ${
                            storeData.industry == option.id
                              ? "bg-green-50 border-l-4 border-l-green-500"
                              : "hover:bg-green-50"
                          } flex align-center items-center gap-3`}
                        >
                          <input
                            onChange={() => {}}
                            type="radio"
                            value={option.id}
                            className="focus:outline-none min-w-5 h-5 mt-2"
                            name={"industry"}
                            id={option.id}
                            checked={storeData.industry == option.id}
                          />
                          <div>
                            <p className="text-sm font-medium">
                              {option.title}
                            </p>
                            <p className="text-xs dark:text-gray-400 leading-1 mt-1">
                              {option.description}
                            </p>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Sub-Industry Selection */}
            {showSubIndustries && selectedIndustry && (
              <div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowSubIndustries(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  <div>
                    <p className="text-lg font-medium mt-6">
                      What specific category within {selectedIndustry.title}{" "}
                      best describe you?
                    </p>
                    <p className="text-sm mt-1">
                      Select the sub-category that best describes your company,
                      team, or department.
                    </p>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="grid lg:grid-cols-2 md:grid-cols-2 gap-4">
                    {selectedIndustry.subIndustries.map((subIndustry) => (
                      <div
                        key={subIndustry.id}
                        onClick={() => handleSubIndustrySelect(subIndustry)}
                        className={`cursor-pointer border rounded-lg p-4 transition-all hover:shadow-md ${
                          storeData.sub_industry === subIndustry.id
                            ? "border-green-500 bg-green-50"
                            : "border-gray-200"
                        }`}
                      >
                        <div className="flex items-center">
                          <input
                            type="radio"
                            checked={storeData.sub_industry === subIndustry.id}
                            onChange={() => {}}
                            className="h-4 w-4 mr-3"
                          />
                          <span className="font-medium">
                            {subIndustry.name}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-4 py-4 pb-0 bottom-0 w-full px-4 lg:px-0 mt-6">
              {showSubIndustries && (
                <button
                  className="flex items-center text-gray-800 text-sm focus:outline-none hover:text-green-500"
                  onClick={() => setShowSubIndustries(false)}
                >
                  <BiChevronLeft className="mr-1 w-4 h-4" />
                  Back
                </button>
              )}
              <Button onClick={handleContinue} className="px-12">
                {showSubIndustries ? "Continue" : "Next"}
              </Button>
            </div>
          </div>
        </OnBoardingContainer>
      </div>
    </div>
  );
}
